import { useMemo } from 'react';
import { Box, Typography, Chip, Paper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDropdownContext } from './DropdownContext';
import { IOption } from './types';

interface DropdownTriggerProps<T extends string | number> {
  open: boolean;
  placeholder: string;
  maxChips: number;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleDeleteChip: (e: React.MouseEvent, idToDelete: T) => void;
}

export const DropdownTrigger = <T extends string | number>({
  open,
  placeholder,
  maxChips,
  handleClick,
  handleDeleteChip
}: DropdownTriggerProps<T>) => {
  
  const { value, options } = useDropdownContext<T>();

  const selectedOptions = useMemo(() => {
    return options.filter((opt: IOption<T>) => value.includes(opt.id));
  }, [options, value]);

  const visibleChips = selectedOptions.slice(0, maxChips);
  const hiddenCount = selectedOptions.length - maxChips;

  return (
    <Paper
      onClick={handleClick}
      sx={{
        borderColor: open ? 'primary.main' : 'divider',
        padding: '8px 12px 8px 12px',
        width: '100%',
        minHeight: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: open ? 'primary.main' : 'text.primary',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, overflow: 'hidden', flexGrow: 1 }}>
        {selectedOptions.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 0.5 }}>
            {placeholder}
          </Typography>
        )}

        {visibleChips.map((opt) => (
          <Chip
            key={opt.id}
            sx={{ maxWidth: '140px' }}
            label={opt.label}
            onDelete={(e) => handleDeleteChip(e, opt.id)}
            onClick={(e) => e.stopPropagation()} 
            size="small"
          />
        ))}

        {hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            variant="outlined"
            sx={{ backgroundColor: 'action.hover' }}
          />
        )}
      </Box>
      <ArrowDropDownIcon color="action" />
    </Paper>
  );
};